import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import StickyQuoteButton from "@/components/feature/StickyQuoteButton";
import { useSEO } from "@/hooks/useSEO";

const whyArcfra = [
  {
    icon: "ri-money-dollar-circle-line",
    title: "Significantly Lower TCO",
    description: "Arcfra's plug-and-play modular architecture and transparent subscription licensing eliminates the complexity and high cost of VMware and Nutanix — customers report significant TCO reductions with less operational overhead.",
  },
  {
    icon: "ri-lock-unlock-line",
    title: "Zero Vendor Lock-in",
    description: "Pure software platform compatible with industry-standard commodity X86 servers. No proprietary hardware dependency, no forced upgrades — full control stays with your IT team.",
  },
  {
    icon: "ri-stack-line",
    title: "Full-Stack Software-Defined Infrastructure",
    description: "AECP converges compute (virtualisation + containers), software-defined storage (block + file), and network & security into one unified platform — one management console for everything.",
  },
  {
    icon: "ri-apps-line",
    title: "Bi-Modal IT on One Platform",
    description: "Run traditional VM workloads (databases, middleware, ERP) alongside cloud-native container workloads and AI/ML on the same AECP platform — no separate infrastructure required.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Proved by 1,000+ Enterprise Customers",
    description: "Arcfra is trusted by over 1,000 enterprise customers worldwide. Recognized by Gartner as one of 8 global representative vendors for Full-Stack HCI Software (April 2025) and as a VMware alternative (March 2025).",
  },
  {
    icon: "ri-customer-service-2-line",
    title: "Local Malaysian Support",
    description: "SE Distribution provides authorised pre-sales, deployment, and post-sales support in Malaysia — no waiting for regional offices or overseas escalation queues.",
  },
];

const products = [
  {
    id: "ave",
    name: "Arcfra Virtualization Engine (AVE)",
    tagline: "Flexible and Open Virtualisation — Full Enterprise-Grade Features",
    specs: [
      { label: "Type", value: "Hypervisor / Virtualisation Engine" },
      { label: "Customers", value: "600+ enterprise customers proved" },
      { label: "Gartner", value: "Customer Choice — VMware Alternative 2025" },
      { label: "Min. Cluster", value: "3 nodes" },
      { label: "Integration", value: "Supports VMware ESXi integration" },
      { label: "Management", value: "Arcfra Operation Center (AOC)" },
    ],
    features: [
      "Full enterprise-grade VM lifecycle management",
      "Live migration with zero downtime",
      "Built-in high availability and automatic failover",
      "Additional features beyond vSphere — easy to use",
      "Seamless integration with Arcfra Block Storage",
      "Compatible with commodity X86 and HCI appliances",
      "One-click OS software upgrade with rolling, online node updates",
      "Arcfra Migration Tools for seamless workload migration",
    ],
  },
  {
    id: "abs",
    name: "Arcfra Block Storage (ABS)",
    tagline: "Stable, Scalable, and High-Performance Software-Defined Storage",
    specs: [
      { label: "Type", value: "Software-Defined Block Storage" },
      { label: "Architecture", value: "Distributed, cloud-native design" },
      { label: "Protocols", value: "iSCSI, NFS, vHost (hyperconverged)" },
      { label: "Data Tiering", value: "Automatic data tiering" },
      { label: "Redundancy", value: "Multi-replica + topology awareness" },
      { label: "DR", value: "Intra-city active-active + cross-city async replication" },
    ],
    features: [
      "Well-designed for cloud with advanced architecture",
      "Automatic data tiering and effective local storage caching",
      "Disk-level data checksum for integrity verification",
      "Node-level multiple replicas for data availability",
      "Chassis topology awareness for fault domain protection",
      "Intra-city active-active clustering (RPO=0, RTO in minutes, RTT ≤5ms)",
      "Cross-city asynchronous replication (RPO ≥15 min, RTO in minutes)",
      "Three centres, two cities disaster recovery architecture",
    ],
  },
  {
    id: "ake",
    name: "Arcfra Kubernetes Engine (AKE)",
    tagline: "Production-Ready Kubernetes Management — Easy to Use, No Vendor Lock-in",
    specs: [
      { label: "Type", value: "Managed Kubernetes Service" },
      { label: "Compliance", value: "CNCF Kubernetes conformant" },
      { label: "Node Types", value: "VM-based and bare-metal K8s" },
      { label: "Addons", value: "Pre-integrated CNI, Ingress, Monitoring" },
      { label: "Autoscaling", value: "Node groups with autoscale" },
      { label: "Management", value: "GUI-based cluster lifecycle management" },
    ],
    features: [
      "One-click workload cluster creation and configuration",
      "Supports virtual machine nodes and physical machine nodes",
      "Flexible cluster config: Kubernetes version, CSI, CNI, monitoring addons",
      "Batch and automated node management with autoscaling",
      "Reliable, high-performance stateful application support",
      "Open and flexible — no vendor lock-in, CNCF conformant",
      "Pre-integrated with Arcfra full-stack HCI (Compute, Storage, Net & Sec)",
      "Suitable for IT Ops and Admin — simplifies K8s management",
    ],
  },
  {
    id: "ans",
    name: "Arcfra Network Service (ANS)",
    tagline: "Unified Network & Security for VMs and Containers",
    specs: [
      { label: "Type", value: "Software-Defined Network & Security" },
      { label: "Firewall", value: "Distributed firewall (allowlist-based)" },
      { label: "Load Balancer", value: "Built-in, active-active + active-backup" },
      { label: "VPC", value: "Dedicated logically isolated VPC spaces" },
      { label: "Gateways", value: "Floating IP, NAT, L3 routing, L2 bridging" },
      { label: "Security", value: "Microsegmentation + zero-trust policy" },
    ],
    features: [
      "Allowlist-based security policies automatically follow VMs — zero-trust",
      "Business-aware security policies based on labels and security groups",
      "One-click VM security incident response to prevent threat spread",
      "Distributed load balancer runs on VM — seamless cloud integration",
      "Supports application high availability and rapid failover",
      "Dedicated VPC network resources within custom isolated spaces",
      "Rich gateway: floating IP, NAT, L3 routing, L2 bridging, route tables",
      "Unified network plane for both VMs and containers",
    ],
  },
  {
    id: "afs",
    name: "Arcfra File Storage (AFS)",
    tagline: "Integrated Enterprise-Grade File Storage — No Additional Hardware Cost",
    specs: [
      { label: "Type", value: "Software-Defined File Storage" },
      { label: "Integration", value: "Built into AECP — no extra hardware" },
      { label: "HA", value: "Auto IP failover — max 90–120s downtime" },
      { label: "Metadata", value: "Consistent even on power failure" },
      { label: "Management", value: "Unified platform — same AOC console" },
      { label: "Use Case", value: "Shared file access for VMs and containers" },
    ],
    features: [
      "Integrated enterprise-grade file storage — requires no additional hardware cost",
      "Unified platform for operations and management alongside VMs",
      "High availability: auto IP failover to healthy file controller if node fails",
      "Maximum end-to-end downtime: 90s/120s with HA triggered",
      "Metadata and data consistency guaranteed even on power failure",
      "Integrated platform for distributed storage and applications",
      "Managed through the same Arcfra Operation Center (AOC) console",
      "Supports both VM and container file access workloads",
    ],
  },
];

const threeWayComparison = [
  {
    category: "Platform Type",
    aecp: "Full-Stack Software-Defined Infrastructure",
    nutanix: "Hyperconverged Infrastructure",
    vmware: "Virtualisation Software (requires separate storage/networking licenses)",
    aecpWin: true,
  },
  {
    category: "Licensing Model",
    aecp: "Subscription — by CPU core, modular",
    nutanix: "Per-node subscription (often bundled)",
    vmware: "Per-socket (Broadcom, significant price increases post-acquisition)",
    aecpWin: true,
  },
  {
    category: "Vendor Lock-in",
    aecp: "None — open X86, pure software",
    nutanix: "Proprietary AOS stack, hardware ecosystem dependency",
    vmware: "High — proprietary ESXi, vSAN, NSX formats",
    aecpWin: true,
  },
  {
    category: "VM Workloads",
    aecp: "Yes — Arcfra Virtualization Engine (AVE), 600+ enterprise customers",
    nutanix: "Yes — AHV hypervisor",
    vmware: "Yes — ESXi (industry incumbent)",
    aecpWin: false,
  },
  {
    category: "Container / Kubernetes",
    aecp: "Yes — Arcfra Kubernetes Engine (AKE), CNCF conformant, bare-metal K8s",
    nutanix: "Yes — Nutanix Kubernetes Engine (NKE)",
    vmware: "Tanzu — separate product, additional cost",
    aecpWin: true,
  },
  {
    category: "Block Storage",
    aecp: "Yes — Arcfra Block Storage (ABS), automatic data tiering, multi-replica",
    nutanix: "Yes — Nutanix Volumes (iSCSI)",
    vmware: "vSAN — separate license required",
    aecpWin: true,
  },
  {
    category: "File Storage",
    aecp: "Yes — Arcfra File Storage (AFS), built-in, no extra hardware",
    nutanix: "Yes — Nutanix Files",
    vmware: "Not included — requires third-party NAS",
    aecpWin: true,
  },
  {
    category: "SDN & Security",
    aecp: "Yes — Arcfra Network Service (ANS), microsegmentation, VPC, distributed firewall",
    nutanix: "Flow Network Security (microsegmentation)",
    vmware: "NSX — separate product, significant additional cost",
    aecpWin: true,
  },
  {
    category: "Disaster Recovery",
    aecp: "Three centres, two cities — RPO=0 (active-active), RPO ≥15min (async cross-city)",
    nutanix: "Leap DR (async replication)",
    vmware: "Site Recovery Manager — separate license",
    aecpWin: true,
  },
  {
    category: "AI / ML Workloads",
    aecp: "Yes — Neutree AI platform + GPU management, private Model-as-a-Service",
    nutanix: "Limited — general VM support for AI",
    vmware: "Limited — no native AI orchestration",
    aecpWin: true,
  },
  {
    category: "Hardware Compatibility",
    aecp: "Commodity X86 servers + HCI appliances — no proprietary hardware",
    nutanix: "Nutanix-certified nodes or NX-series hardware",
    vmware: "Broad HCL — but vSAN requires certified nodes",
    aecpWin: true,
  },
  {
    category: "Analyst Recognition",
    aecp: "Gartner: VMware Alt Guide Mar 2025 + Full-Stack HCI Apr 2025; DCIG Top 5 2026–27",
    nutanix: "Gartner Magic Quadrant Leader (HCI)",
    vmware: "Gartner Magic Quadrant Leader (legacy position)",
    aecpWin: false,
  },
];

const useCases = [
  {
    icon: "ri-building-2-line",
    title: "Enterprise IT Modernisation",
    description: "Replace aging VMware vSphere clusters with AECP — consolidating servers, reducing licensing costs, and simplifying management. Infrastructure provisioning in a few hours, starting from 3 nodes.",
  },
  {
    icon: "ri-bank-line",
    title: "Financial Services",
    description: "High-availability virtualisation for core banking, trading platforms, and compliance workloads — with built-in microsegmentation, active-active clustering, and full data sovereignty on-premise.",
  },
  {
    icon: "ri-hospital-line",
    title: "Healthcare & Hospitals",
    description: "Reliable HCI for PACS imaging, EMR systems, and clinical applications with intra-city active-active clustering (RPO=0) and cross-city async DR for Malaysian healthcare data residency requirements.",
  },
  {
    icon: "ri-government-line",
    title: "Government & Public Sector",
    description: "Sovereign cloud infrastructure for government agencies — replacing VMware with an open-standards AECP platform that keeps data within Malaysian borders with full audit trail and microsegmentation.",
  },
];

const migrationSteps = [
  { step: "01", title: "Assessment", desc: "We audit your current VMware or Nutanix environment — inventory, workloads, dependencies, and licensing costs." },
  { step: "02", title: "Architecture Design", desc: "Our engineers design the AECP architecture sized for your workloads, with a phased migration plan using Arcfra Migration Tools." },
  { step: "03", title: "Pilot Deployment", desc: "Deploy a 3-node AECP cluster in parallel with your existing environment — migrate non-critical workloads first using AVE's seamless migration tools." },
  { step: "04", title: "Live Migration", desc: "Migrate production VMs with zero downtime using Arcfra's live migration. Flexible, efficient, time-saving — proven across numerous customer deployments." },
  { step: "05", title: "Decommission & Save", desc: "Retire VMware/Nutanix licenses and hardware — realise immediate cost savings from day one of full operation with significantly lower TCO." },
];

const arcfraProductSchema = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": "Arcfra AECP Product Portfolio — SE Distribution Malaysia",
  "description": "Arcfra Enterprise Cloud Platform (AECP) products available from SE Distribution, authorized Arcfra distributor in Malaysia.",
  "url": "https://www.sedistribution.com.my/arcfra-spotlight",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "item": {
        "@type": "Product",
        "name": "Arcfra Virtualization Engine (AVE)",
        "description": "Enterprise-grade hypervisor and virtualisation engine. Full VM lifecycle management, live migration, HA, automatic failover. 600+ enterprise customers. Recognized by Gartner as VMware alternative (March 2025). Compatible with VMware ESXi integration and commodity X86 servers.",
        "brand": { "@type": "Brand", "name": "Arcfra" },
        "sku": "AVE",
        "url": "https://www.sedistribution.com.my/arcfra-spotlight",
        "additionalProperty": [
          { "@type": "PropertyValue", "name": "Customers", "value": "600+ enterprise customers" },
          { "@type": "PropertyValue", "name": "Gartner Recognition", "value": "VMware Alternative Guide March 2025" },
          { "@type": "PropertyValue", "name": "Minimum Cluster", "value": "3 nodes" },
          { "@type": "PropertyValue", "name": "Migration", "value": "Arcfra Migration Tools — seamless VMware migration" }
        ],
        "offers": {
          "@type": "Offer",
          "availability": "https://schema.org/InStock",
          "priceCurrency": "MYR",
          "seller": { "@type": "Organization", "name": "SE Distribution Sdn Bhd" }
        }
      }
    },
    {
      "@type": "ListItem",
      "position": 2,
      "item": {
        "@type": "Product",
        "name": "Arcfra Block Storage (ABS)",
        "description": "Software-defined block storage with automatic data tiering, multi-replica redundancy, topology awareness, intra-city active-active clustering (RPO=0, RTO in minutes), and cross-city async replication (RPO ≥15 min).",
        "brand": { "@type": "Brand", "name": "Arcfra" },
        "sku": "ABS",
        "url": "https://www.sedistribution.com.my/arcfra-spotlight",
        "additionalProperty": [
          { "@type": "PropertyValue", "name": "DR Topology", "value": "Three centres, two cities" },
          { "@type": "PropertyValue", "name": "Active-Active RTT", "value": "≤5ms, bandwidth ≥10Gb/s" },
          { "@type": "PropertyValue", "name": "Protocols", "value": "iSCSI, NFS, vHost" }
        ],
        "offers": {
          "@type": "Offer",
          "availability": "https://schema.org/InStock",
          "priceCurrency": "MYR",
          "seller": { "@type": "Organization", "name": "SE Distribution Sdn Bhd" }
        }
      }
    },
    {
      "@type": "ListItem",
      "position": 3,
      "item": {
        "@type": "Product",
        "name": "Arcfra Kubernetes Engine (AKE)",
        "description": "Production-ready CNCF-conformant Kubernetes management. One-click cluster creation, VM and bare-metal K8s, autoscaling node groups, pre-integrated CNI/Ingress/Monitoring. No vendor lock-in.",
        "brand": { "@type": "Brand", "name": "Arcfra" },
        "sku": "AKE",
        "url": "https://www.sedistribution.com.my/arcfra-spotlight",
        "additionalProperty": [
          { "@type": "PropertyValue", "name": "Compliance", "value": "CNCF Kubernetes conformant" },
          { "@type": "PropertyValue", "name": "Node Support", "value": "VM-based and bare-metal" }
        ],
        "offers": {
          "@type": "Offer",
          "availability": "https://schema.org/InStock",
          "priceCurrency": "MYR",
          "seller": { "@type": "Organization", "name": "SE Distribution Sdn Bhd" }
        }
      }
    }
  ]
};

const arcfraFAQSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Is Arcfra a good VMware alternative in Malaysia?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. Arcfra AECP is a proven VMware vSphere and Nutanix AHV alternative recognized by Gartner in both the March 2025 'Guide to Choosing a VMware Alternative' and the April 2025 'Market Guide for Full-Stack HCI Software' as one of 8 worldwide representative vendors. It is trusted by 1,000+ enterprise customers globally, uses open-standards commodity X86 hardware with no vendor lock-in, and SE Distribution provides full local support in Malaysia."
      }
    },
    {
      "@type": "Question",
      "name": "How long does a VMware to Arcfra migration take?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "A typical VMware to Arcfra migration follows a 5-step process: assessment, architecture design, pilot deployment, live migration, and decommission. Arcfra provides dedicated Migration Tools that are flexible, efficient, and time-saving — proven across numerous customer deployments. SE Distribution handles the entire process with zero production downtime."
      }
    },
    {
      "@type": "Question",
      "name": "What is the minimum cluster size for Arcfra AECP?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Arcfra AECP requires a minimum of 3 nodes to form a cluster with full high availability and distributed storage. The platform offers great scalability — starting from 3 nodes — and supports infrastructure provisioning in a few hours."
      }
    },
    {
      "@type": "Question",
      "name": "Does Arcfra support Kubernetes and containers?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. Arcfra includes a dedicated Arcfra Kubernetes Engine (AKE) that is CNCF Kubernetes conformant. It supports one-click cluster creation, VM-based and bare-metal K8s nodes, autoscaling, and pre-integrated CNI/Ingress/Monitoring. AECP supports bi-modal IT — running traditional VM workloads and cloud-native container workloads on the same platform."
      }
    }
  ]
};

const neutreeFeatures = [
  {
    icon: "ri-database-2-line",
    title: "Model Registry & Dataset Management",
    desc: "Centralised model registry with dataset management, version control, and private model training management — all within your own infrastructure.",
  },
  {
    icon: "ri-cpu-line",
    title: "GPU Management & Compute Resources",
    desc: "Unified compute resource management across GPU-accelerated servers. Schedule and allocate GPU capacity for model training, fine-tuning, and inference workloads.",
  },
  {
    icon: "ri-server-line",
    title: "Model Serving Infrastructure",
    desc: "Production-ready inference services with high availability and intelligent failover. Built for resilient AI operations at enterprise scale.",
  },
  {
    icon: "ri-git-branch-line",
    title: "Private Model Training & Fine-Tuning",
    desc: "Secure, isolated environment for training and fine-tuning LLMs and ML models on proprietary data — no data leaves your on-premise infrastructure.",
  },
  {
    icon: "ri-flow-chart",
    title: "Unified VM + Kubernetes for AI",
    desc: "Deep integration with AECP provides a unified VM + Kubernetes environment spanning model training, fine-tuning, and inference — on the same platform as your existing workloads.",
  },
  {
    icon: "ri-eye-line",
    title: "Unified Observability",
    desc: "Integrated monitoring, logging, and alerting across all AI infrastructure. Full traffic visibility with application-level microsegmentation via Arcfra Network Service (ANS).",
  },
];

const arcfraBreadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://www.sedistribution.com.my/"
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "Solutions",
      "item": "https://www.sedistribution.com.my/solutions"
    },
    {
      "@type": "ListItem",
      "position": 3,
      "name": "HCI & Storage Infrastructure",
      "item": "https://www.sedistribution.com.my/solutions/data-protection"
    },
    {
      "@type": "ListItem",
      "position": 4,
      "name": "Arcfra AECP Spotlight",
      "item": "https://www.sedistribution.com.my/arcfra-spotlight"
    }
  ]
};

export default function ArcfraSpotlightPage() {
  useSEO({
    title: "Arcfra AECP Malaysia | VMware & Nutanix Alternative | HCI Platform | SE Distribution",
    description: "Replace VMware vSphere & Nutanix with Arcfra Enterprise Cloud Platform (AECP) — Gartner-recognized HCI, 1000+ enterprise customers, no vendor lock-in. Authorized Arcfra distributor in Malaysia. Free migration assessment.",
    canonical: "https://www.sedistribution.com.my/arcfra-spotlight",
    keywords: "Arcfra Malaysia, VMware alternative Malaysia, Nutanix alternative Malaysia, HCI Malaysia, AECP Malaysia, hyperconverged infrastructure Malaysia, Arcfra Virtualization Engine, VMware migration Malaysia",
    structuredData: arcfraBreadcrumbSchema,
  });
  const [activeProduct, setActiveProduct] = useState("ave");
  const currentProduct = products.find((p) => p.id === activeProduct) || products[0];

  return (
    <div className="min-h-screen bg-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(arcfraProductSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(arcfraFAQSchema) }}
      />
      <Navbar />
      <StickyQuoteButton label="Get a Free Assessment" />

      {/* Hero */}
      <section className="relative w-full h-[520px] md:h-[620px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=modern%20enterprise%20data%20center%20server%20racks%20rows%20glowing%20LED%20lights%20hyperconverged%20infrastructure%20dramatic%20cinematic%20perspective%2C%20professional%20photography%2C%20wide%20angle%2C%20dark%20moody%20atmosphere&width=1400&height=700&seq=arcfra-hero&orientation=landscape"
          alt="Arcfra AECP Platform"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/20"></div>
        <div className="absolute inset-0 flex flex-col justify-center px-6 lg:px-20 max-w-7xl mx-auto w-full">
          <nav className="flex items-center gap-2 text-xs text-white/50 mb-6">
            <Link to="/" className="hover:text-white/80 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/solutions/data-protection" className="hover:text-white/80 transition-colors">HCI &amp; Storage</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white/80">Arcfra Spotlight</span>
          </nav>
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-4 w-fit">
            <i className="ri-exchange-line text-white/70 text-xs"></i>
            <span className="text-xs font-semibold text-white/80 uppercase tracking-widest">VMware &amp; Nutanix Alternative</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight mb-4 max-w-3xl">
            Arcfra AECP — Malaysia's Enterprise Cloud Platform
          </h1>
          <p className="text-lg text-white/75 max-w-xl leading-relaxed mb-3">
            Arcfra Enterprise Cloud Platform (AECP) is a full-stack software-defined infrastructure — converging virtualisation, containers, storage, and networking into one unified platform. Recognised by Gartner. Trusted by 1,000+ enterprises globally.
          </p>
          <p className="text-sm text-white/50 mb-8">Authorised Distributor · Headquartered in Singapore · Local Pre-Sales &amp; Post-Sales Support in Malaysia</p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/arcfra-assessment"
              className="bg-white text-[#1a1a1a] text-sm font-semibold px-7 py-3.5 rounded-md hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2 w-fit"
            >
              Book Your Free Assessment <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/solutions/data-protection"
              className="border border-white/50 text-white text-sm font-medium px-7 py-3.5 rounded-md hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer w-fit"
            >
              View HCI Solutions
            </Link>
          </div>
        </div>
      </section>

      {/* Gartner Recognition Banner */}
      <section className="bg-[#f8f7f4] border-b border-gray-100 py-5">
        <div className="max-w-7xl mx-auto px-6 lg:px-20 flex flex-col md:flex-row items-center justify-center gap-6 md:gap-14">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-[#1a1a1a] rounded-md flex-shrink-0">
              <i className="ri-award-line text-white text-sm"></i>
            </div>
            <p className="text-xs text-[#1a1a1a]">
              <strong>Gartner March 2025</strong> — <span className="text-gray-500">Recognized in "Guide to Choosing a VMware Alternative in the Wake of Broadcom Acquisition"</span>
            </p>
          </div>
          <div className="hidden md:block w-px h-8 bg-gray-200"></div>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-[#1a1a1a] rounded-md flex-shrink-0">
              <i className="ri-award-line text-white text-sm"></i>
            </div>
            <p className="text-xs text-[#1a1a1a]">
              <strong>Gartner April 2025</strong> — <span className="text-gray-500">1 of 8 worldwide representative vendors in "Market Guide for Full-Stack HCI Software"</span>
            </p>
          </div>
          <div className="hidden md:block w-px h-8 bg-gray-200"></div>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-[#1a1a1a] rounded-md flex-shrink-0">
              <i className="ri-medal-line text-white text-sm"></i>
            </div>
            <p className="text-xs text-[#1a1a1a]">
              <strong>DCIG 2026–27</strong> — <span className="text-gray-500">Top 5 VMware Alternatives — Rising Vendors Edition</span>
            </p>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#1a1a1a] py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-20 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { value: "1,000+", label: "Enterprise Customers Worldwide" },
            { value: "3 nodes", label: "Minimum Cluster to Start" },
            { value: "5 modules", label: "AVE · ABS · AKE · ANS · AFS" },
            { value: "0", label: "Vendor Lock-in (Open X86)" },
          ].map((s) => (
            <div key={s.label} className="text-center">
              <p className="text-xl md:text-2xl font-bold text-white mb-1">{s.value}</p>
              <p className="text-xs text-white/50 uppercase tracking-wider">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Why Arcfra */}
      <section className="py-20 px-6 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 block">Why Arcfra</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a]">Why Malaysian Enterprises Choose Arcfra AECP</h2>
            <p className="text-gray-500 text-base mt-3 max-w-xl mx-auto">
              As VMware licensing costs surge post-Broadcom acquisition, Arcfra delivers a Gartner-recognized migration path with no compromise on enterprise capabilities — starting from just 3 nodes.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyArcfra.map((item) => (
              <div key={item.title} className="bg-[#f8f7f4] rounded-lg p-7 hover:bg-[#f0ede8] transition-colors">
                <div className="w-12 h-12 flex items-center justify-center bg-[#1a1a1a] rounded-lg mb-5">
                  <i className={`${item.icon} text-white text-xl`}></i>
                </div>
                <h3 className="font-bold text-[#1a1a1a] text-base mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Tabs */}
      <section className="bg-[#f8f7f4] py-20 px-6 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 block">AECP Modules</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a]">Arcfra Enterprise Cloud Platform — 5 Core Modules</h2>
            <p className="text-gray-500 text-sm mt-3 max-w-2xl mx-auto">AECP is a modular, full-stack software-defined infrastructure platform. Each module is individually deployable and deeply integrated through the unified Arcfra Operation Center (AOC).</p>
          </div>

          {/* Tab Switcher */}
          <div className="flex flex-wrap gap-2 justify-center mb-10">
            {products.map((p) => (
              <button
                key={p.id}
                onClick={() => setActiveProduct(p.id)}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeProduct === p.id
                    ? "bg-[#1a1a1a] text-white"
                    : "bg-white text-gray-500 hover:text-[#1a1a1a] border border-gray-200"
                }`}
              >
                {p.name.split(" (")[0]}
              </button>
            ))}
          </div>

          {/* Product Detail */}
          <div className="bg-white rounded-xl overflow-hidden border border-gray-100">
            <div className="p-8 lg:p-10">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1 block">Arcfra AECP</span>
              <h3 className="text-2xl font-bold text-[#1a1a1a] mb-1">{currentProduct.name}</h3>
              <p className="text-sm text-gray-500 mb-6">{currentProduct.tagline}</p>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
                {currentProduct.specs.map((spec) => (
                  <div key={spec.label} className="bg-[#f8f7f4] rounded-lg px-4 py-3">
                    <p className="text-xs text-gray-400 mb-0.5">{spec.label}</p>
                    <p className="text-sm font-semibold text-[#1a1a1a]">{spec.value}</p>
                  </div>
                ))}
              </div>

              <div className="mb-6">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Key Features</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {currentProduct.features.map((f) => (
                    <div key={f} className="flex items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-[#1a1a1a] text-sm"></i>
                      </div>
                      <span className="text-sm text-gray-600">{f}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-[#1a1a1a] text-white text-sm font-semibold px-6 py-3 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer"
              >
                Request a Demo <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Supported Workloads */}
      <section className="py-20 px-6 lg:px-20 bg-[#f8f7f4]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 block">Deployment Scenarios</span>
            <h2 className="text-3xl font-bold text-[#1a1a1a]">One Platform. Three Workload Categories.</h2>
            <p className="text-gray-500 text-sm mt-3 max-w-xl mx-auto">
              AECP supports bi-modal IT on a single platform — run traditional VM applications, cloud-native containerised workloads, and AI/ML inference side by side, managed from one console.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Virtualised Workloads */}
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden flex flex-col">
              <div className="bg-[#1a1a1a] px-7 py-5">
                <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-lg mb-3">
                  <i className="ri-server-line text-white text-xl"></i>
                </div>
                <h3 className="text-lg font-bold text-white mb-1">Virtualised Workloads</h3>
                <p className="text-white/55 text-xs leading-relaxed">Traditional enterprise apps running on VMs — powered by Arcfra Virtualization Engine (AVE)</p>
              </div>
              <div className="px-7 py-6 flex-1">
                <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">Typical Applications</p>
                <ul className="space-y-2.5 mb-6">
                  {[
                    "ERP & CRM systems (SAP, Oracle, Salesforce on-prem)",
                    "Databases (SQL Server, Oracle DB, PostgreSQL)",
                    "Virtual Desktop Infrastructure (VDI)",
                    "Middleware & application servers",
                    "File & collaboration services",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-gray-600">
                      <i className="ri-checkbox-circle-line text-[#1a1a1a] mt-0.5 flex-shrink-0 text-base"></i>
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="border-t border-gray-100 pt-4">
                  <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">AECP Modules Used</p>
                  <div className="flex flex-wrap gap-1.5">
                    {["AVE (Virtualisation)", "ABS (Block Storage)", "ANS (Network)", "AFS (File Storage)"].map((m) => (
                      <span key={m} className="text-[10px] font-bold bg-[#1a1a1a] text-white px-2 py-1 rounded-full">{m}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Cloud-Native Workloads */}
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden flex flex-col">
              <div className="bg-[#2d2d2d] px-7 py-5">
                <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-lg mb-3">
                  <i className="ri-cloud-line text-white text-xl"></i>
                </div>
                <h3 className="text-lg font-bold text-white mb-1">Cloud-Native Workloads</h3>
                <p className="text-white/55 text-xs leading-relaxed">Microservices and containerised apps — powered by Arcfra Kubernetes Engine (AKE)</p>
              </div>
              <div className="px-7 py-6 flex-1">
                <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">Typical Applications</p>
                <ul className="space-y-2.5 mb-6">
                  {[
                    "Microservices & containerised applications",
                    "DevOps CI/CD pipelines",
                    "Stateful Kubernetes workloads",
                    "Modern SaaS-style enterprise apps",
                    "API gateways & service meshes",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-gray-600">
                      <i className="ri-checkbox-circle-line text-[#1a1a1a] mt-0.5 flex-shrink-0 text-base"></i>
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="border-t border-gray-100 pt-4">
                  <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">AECP Modules Used</p>
                  <div className="flex flex-wrap gap-1.5">
                    {["AKE (Kubernetes)", "ABS (Block Storage)", "ANS (Network & Security)"].map((m) => (
                      <span key={m} className="text-[10px] font-bold bg-[#1a1a1a] text-white px-2 py-1 rounded-full">{m}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* AI/ML Workloads */}
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden flex flex-col">
              <div className="bg-[#404040] px-7 py-5">
                <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-lg mb-3">
                  <i className="ri-brain-line text-white text-xl"></i>
                </div>
                <h3 className="text-lg font-bold text-white mb-1">AI &amp; ML Workloads</h3>
                <p className="text-white/55 text-xs leading-relaxed">Private AI infrastructure — powered by Neutree + AECP on GPU-accelerated servers</p>
              </div>
              <div className="px-7 py-6 flex-1">
                <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">Typical Applications</p>
                <ul className="space-y-2.5 mb-6">
                  {[
                    "LLM model training & fine-tuning on private data",
                    "Private inference services (Model-as-a-Service)",
                    "GPU-accelerated compute workloads",
                    "AI data pipelines: raw datasets → vector DBs",
                    "Model registry & experiment tracking",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-gray-600">
                      <i className="ri-checkbox-circle-line text-[#1a1a1a] mt-0.5 flex-shrink-0 text-base"></i>
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="border-t border-gray-100 pt-4">
                  <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">AECP Modules Used</p>
                  <div className="flex flex-wrap gap-1.5">
                    {["Neutree AI Platform", "ABS (Block Storage)", "AFS (File Storage)", "AKE (Kubernetes)"].map((m) => (
                      <span key={m} className="text-[10px] font-bold bg-[#1a1a1a] text-white px-2 py-1 rounded-full">{m}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Bottom callout */}
          <div className="mt-8 bg-white border border-gray-100 rounded-xl px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-[#1a1a1a]/5 rounded-lg flex-shrink-0">
                <i className="ri-layout-grid-line text-[#1a1a1a] text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-bold text-[#1a1a1a]">All three workload types run on the same AECP cluster</p>
                <p className="text-xs text-gray-400 mt-0.5">Managed from a single Arcfra Operation Center (AOC) console — no separate infrastructure needed for VMs, containers, or AI.</p>
              </div>
            </div>
            <Link
              to="/arcfra-assessment"
              className="inline-flex items-center gap-2 bg-[#1a1a1a] text-white text-sm font-semibold px-6 py-3 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer flex-shrink-0"
            >
              Get a Free Assessment <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* AECP vs Nutanix vs VMware Comparison Table */}
      <section className="py-20 px-6 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 block">Head-to-Head</span>
            <h2 className="text-3xl font-bold text-[#1a1a1a]">Arcfra AECP vs Nutanix vs VMware</h2>
            <p className="text-gray-500 text-sm mt-3 max-w-2xl mx-auto">
              All claims are based on publicly available product documentation and the Arcfra AECP enablement PDF. Arcfra AECP is the only platform in this comparison with native AI infrastructure (Neutree), built-in file storage at no extra hardware cost, and a three-centre disaster recovery topology.
            </p>
          </div>
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full text-sm min-w-[900px]">
              <thead>
                <tr className="bg-[#1a1a1a]">
                  <th className="text-left text-xs font-bold text-white/60 uppercase tracking-wider px-5 py-4 w-[20%]">Capability</th>
                  <th className="text-center px-5 py-4 w-[27%]">
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-[10px] font-bold bg-white/20 text-white px-2 py-0.5 rounded-full">Our Pick</span>
                      <span className="text-sm font-bold text-white">Arcfra AECP</span>
                    </div>
                  </th>
                  <th className="text-center px-5 py-4 w-[27%]">
                    <span className="text-sm font-bold text-white/70">Nutanix</span>
                  </th>
                  <th className="text-center px-5 py-4 w-[26%]">
                    <span className="text-sm font-bold text-white/70">VMware (Broadcom)</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {threeWayComparison.map((row, i) => (
                  <tr key={row.category} className={i % 2 === 0 ? "bg-white" : "bg-[#fafaf9]"}>
                    <td className="px-5 py-4 text-xs font-bold text-[#1a1a1a] align-top">{row.category}</td>
                    <td className={`px-5 py-4 text-xs align-top ${
                      row.aecpWin ? "text-[#1a1a1a] font-semibold" : "text-gray-600"
                    }`}>
                      <div className="flex items-start gap-2">
                        {row.aecpWin && (
                          <i className="ri-checkbox-circle-fill text-[#1a1a1a] mt-0.5 flex-shrink-0 text-sm"></i>
                        )}
                        <span>{row.aecp}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-xs text-gray-500 align-top">{row.nutanix}</td>
                    <td className="px-5 py-4 text-xs text-gray-400 align-top">{row.vmware}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-6 flex flex-wrap gap-5 justify-center text-xs text-gray-400">
            <span className="flex items-center gap-1.5"><i className="ri-checkbox-circle-fill text-[#1a1a1a]"></i> AECP advantage in this category</span>
            <span className="flex items-center gap-1.5"><i className="ri-information-line"></i> Based on Arcfra AECP enablement documentation and public product pages</span>
          </div>
          <div className="mt-8 text-center">
            <Link
              to="/arcfra-assessment"
              className="inline-flex items-center gap-2 bg-[#1a1a1a] text-white text-sm font-semibold px-7 py-3.5 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer"
            >
              Get a Free Assessment &amp; TCO Comparison
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Neutree AI Infrastructure */}
      <section className="py-20 px-6 lg:px-20 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
            <div>
              <span className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-3 block">AI Infrastructure</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
                Neutree — Enterprise Private<br />Model-as-a-Service Platform
              </h2>
              <p className="text-white/60 text-sm leading-relaxed mb-5">
                Neutree is an open-source platform built for the enterprise AI journey. Deeply integrated with AECP, it provides a unified VM + Kubernetes environment for hybrid AI workloads — spanning model training, fine-tuning, and inference — entirely within your private infrastructure.
              </p>
              <div className="space-y-3 mb-7">
                {[
                  { label: "Data Flow", val: "Secure, streamlined pipeline from raw datasets to labelled data, model registry, and vector databases" },
                  { label: "Storage", val: "High-performance block and file storage (ABS + AFS) optimised for every stage of the AI data lifecycle" },
                  { label: "Security", val: "Application-level microsegmentation via ANS with full traffic visibility" },
                  { label: "Observability", val: "Unified monitoring, logging, and alerting across all AI infrastructure" },
                  { label: "Availability", val: "Highly available, intelligent production environments built for resilient AI operations" },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-3">
                    <i className="ri-check-line text-white/60 mt-0.5 flex-shrink-0"></i>
                    <p className="text-sm text-white/60"><strong className="text-white/90">{item.label}:</strong> {item.val}</p>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 mb-8">
                {["Model Registry", "GPU Management", "Inference Services", "Private Training", "Fine-Tuning", "Vector DB"].map((tag) => (
                  <span key={tag} className="bg-white/10 text-white/70 text-xs font-medium px-3 py-1.5 rounded-full">{tag}</span>
                ))}
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-white text-[#1a1a1a] text-sm font-semibold px-6 py-3 rounded-md hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                Enquire About AI Infrastructure
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {neutreeFeatures.map((f) => (
                <div key={f.title} className="bg-white/5 border border-white/10 rounded-lg p-5 hover:bg-white/10 transition-colors">
                  <div className="w-9 h-9 flex items-center justify-center bg-white/10 rounded-lg mb-3">
                    <i className={`${f.icon} text-white text-base`}></i>
                  </div>
                  <h4 className="font-semibold text-white text-sm mb-1.5">{f.title}</h4>
                  <p className="text-xs text-white/50 leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Migration Journey */}
      <section className="py-20 px-6 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 block">Migration Path</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a]">Your VMware to Arcfra Journey</h2>
            <p className="text-gray-500 text-base mt-3 max-w-xl mx-auto">
              A structured, zero-disruption migration process — from assessment to full production operation using Arcfra's proven Migration Tools.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
            {migrationSteps.map((step, i) => (
              <div key={step.step} id={`step-${step.step}`} className="relative">
                <div className={`rounded-lg p-6 h-full ${
                  step.step === "01"
                    ? "bg-[#1a1a1a] ring-2 ring-[#1a1a1a]"
                    : "bg-[#f8f7f4]"
                }`}>
                  {step.step === "01" && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-white/60 uppercase tracking-widest bg-white/10 rounded-full px-2 py-0.5 mb-2">
                      <i className="ri-map-pin-line text-[10px]"></i> Start Here
                    </span>
                  )}
                  <span className={`text-3xl font-bold block mb-3 ${
                    step.step === "01" ? "text-white/20" : "text-[#1a1a1a]/10"
                  }`}>{step.step}</span>
                  <h3 className={`font-bold text-sm mb-2 ${
                    step.step === "01" ? "text-white" : "text-[#1a1a1a]"
                  }`}>{step.title}</h3>
                  <p className={`text-xs leading-relaxed ${
                    step.step === "01" ? "text-white/60" : "text-gray-500"
                  }`}>{step.desc}</p>
                </div>
                {i < migrationSteps.length - 1 && (
                  <div className="hidden lg:flex absolute top-1/2 -right-2 w-4 h-4 items-center justify-center z-10">
                    <i className="ri-arrow-right-s-line text-gray-300 text-lg"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="bg-[#1a1a1a] rounded-xl px-8 py-7 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div className="flex items-start gap-5">
              <div className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-xl flex-shrink-0">
                <i className="ri-calendar-check-line text-white text-xl"></i>
              </div>
              <div>
                <div className="inline-flex items-center gap-1.5 bg-white/10 rounded-full px-3 py-1 mb-2">
                  <i className="ri-map-pin-line text-white/60 text-xs"></i>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-white/60">Step 01 — Start Here</span>
                </div>
                <p className="text-base font-bold text-white mb-1">Ready to begin? Book a free infrastructure assessment.</p>
                <p className="text-sm text-white/55 max-w-lg">Our KL-based engineers audit your VMware or Nutanix environment, calculate your exact savings potential, and design a zero-downtime migration plan — at no cost and no commitment.</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
              <Link
                to="/arcfra-assessment"
                className="bg-white text-[#1a1a1a] text-sm font-bold px-7 py-3.5 rounded-md hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2 justify-center"
              >
                Book Free Assessment <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/vmware-alternative-malaysia"
                className="border border-white/30 text-white text-sm font-medium px-6 py-3.5 rounded-md hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2 justify-center"
              >
                View Pricing &amp; TCO
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="bg-[#1a1a1a] py-20 px-6 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-3 block">Use Cases</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white">Who Benefits from Arcfra AECP</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {useCases.map((uc) => (
              <div key={uc.title} className="bg-white/5 rounded-lg p-6 hover:bg-white/10 transition-colors">
                <div className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-lg mb-4">
                  <i className={`${uc.icon} text-white text-xl`}></i>
                </div>
                <h3 className="font-bold text-white text-sm mb-2">{uc.title}</h3>
                <p className="text-xs text-white/50 leading-relaxed">{uc.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 lg:px-20">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[#f8f7f4] rounded-xl p-6 mb-10 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-0">
                {["Arcfra Spotlight", "Free Assessment", "Migration Path"].map((label, idx) => (
                  <div key={label} className="flex items-center gap-0">
                    <span className={`text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap ${
                      idx === 0
                        ? "bg-[#1a1a1a] text-white"
                        : "bg-white border border-gray-200 text-gray-400"
                    }`}>
                      {idx + 1}. {label}
                    </span>
                    {idx < 2 && <i className="ri-arrow-right-s-line text-gray-300 mx-1"></i>}
                  </div>
                ))}
              </div>
            </div>
            <p className="text-xs text-gray-400 sm:text-right">
              You&apos;re here — complete your assessment to begin the migration journey.
            </p>
          </div>

          {/* VMware Alternative Malaysia cross-link band */}
          <div className="bg-[#f8f7f4] rounded-xl px-8 py-5 mb-10 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-[#1a1a1a]/8 rounded-lg flex-shrink-0">
                <i className="ri-exchange-line text-[#1a1a1a] text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-bold text-[#1a1a1a]">Ready to get a VMware migration quote?</p>
                <p className="text-xs text-gray-400 mt-0.5">See pricing, TCO savings, and request a free assessment on our dedicated Malaysia landing page.</p>
              </div>
            </div>
            <Link
              to="/vmware-alternative-malaysia"
              className="inline-flex items-center gap-2 bg-[#1a1a1a] text-white text-sm font-semibold px-6 py-3 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer flex-shrink-0"
            >
              View VMware Alternative Malaysia <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">
              Ready to Modernise Your Infrastructure with AECP?
            </h2>
            <p className="text-gray-500 text-base mb-8 max-w-xl mx-auto">
              Start with a free infrastructure assessment — we'll audit your environment, calculate savings, and map out a zero-downtime migration plan using Arcfra's proven Migration Tools.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/arcfra-assessment"
                className="bg-[#1a1a1a] text-white text-sm font-semibold px-8 py-3.5 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2 justify-center"
              >
                Book Free Assessment — Step 1 of 3 <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/solutions/data-protection"
                className="border border-[#1a1a1a] text-[#1a1a1a] text-sm font-medium px-8 py-3.5 rounded-md hover:bg-[#f8f7f4] transition-colors whitespace-nowrap cursor-pointer"
              >
                View Full Migration Path
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
